import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, UserCheck, Heart, Building2, ShieldCheck, X, Award, Quote, ChevronRight } from "lucide-react";

const features = [
  {
    title: "Benefits",
    description: "Gain access to world-class facilities, cutting-edge technology, and industry-standard training.",
    icon: <Star className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-500" />,
    color: "from-yellow-100 to-yellow-200",
    detailContent: `Access to state-of-the-art laboratories, modern equipment, and cutting-edge technology that prepares you for real-world challenges. Our facilities are constantly updated to meet industry standards and provide hands-on learning experiences.`,
    image: "/images/tes1.jpg",
  },
  {
    title: "Self Development",
    description: "Empowering students with resources, mentorship, and opportunities for personal growth.",
    icon: <UserCheck className="w-8 h-8 sm:w-10 sm:h-10 text-blue-500" />,
    color: "from-blue-100 to-blue-200",
    detailContent: `Our comprehensive personal development programs focus on building leadership skills, communication abilities, and professional competencies. Through workshops, seminars, and mentorship programs, we help you realize your full potential.`,
    image: "/images/tes2.jpg",
  },
  {
    title: "Spirituality",
    description: "We nurture moral values and spiritual well-being to build responsible future leaders.",
    icon: <Heart className="w-8 h-8 sm:w-10 sm:h-10 text-pink-500" />,
    color: "from-pink-100 to-pink-200",
    detailContent: `We believe that true education encompasses not just technical skills but also moral and spiritual development. Our programs promote ethical behavior, community service, and a sense of purpose beyond academic achievement.`,
    image: "/images/tes3.jpg",
  },
  {
    title: "Campus Life",
    description: "Experience vibrant campus culture, extracurricular activities, and a supportive community.",
    icon: <Building2 className="w-8 h-8 sm:w-10 sm:h-10 text-green-500" />,
    color: "from-green-100 to-green-200",
    detailContent: `Our vibrant campus community offers numerous opportunities for students to engage in extracurricular activities, clubs, and social events. Build lasting friendships and create memories that will last a lifetime.`,
    image: "/images/campus-life.jpg",
  },
  {
    title: "Security",
    description: "A safe and secure environment where students can focus on their studies without worry.",
    icon: <ShieldCheck className="w-8 h-8 sm:w-10 sm:h-10 text-purple-500" />,
    color: "from-purple-100 to-purple-200",
    detailContent: `Our comprehensive security system includes 24/7 surveillance, trained security personnel, and emergency response protocols. Your safety is our top priority, allowing you to focus entirely on your education and personal growth.`,
    image: "/images/security.jpg",
  },
  {
    title: "Sports Facilities",
    description: "State-of-the-art sports facilities to promote physical fitness and well-being.",
    icon: <Star className="w-8 h-8 sm:w-10 sm:h-10 text-orange-500" />,
    color: "from-orange-100 to-orange-200",
    detailContent: `Our comprehensive sports facilities include modern gyms, playing fields, and recreational areas. We encourage physical fitness through various sports programs, competitions, and wellness activities.`,
    image: "/images/sports.jpg",
  },
];

const containerVariants = { 
  visible: { transition: { staggerChildren: 0.15 } } 
};

const cardVariants = { 
  hidden: { opacity: 0, y: 40 }, 
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
};

const sparklesCount = 12;

const WhyChooseUs = () => {
  const [selected, setSelected] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [sparkles, setSparkles] = useState([]);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    // Generate sparkles
    const particles = Array.from({ length: sparklesCount }).map(() => ({
      xOffset: 10 + Math.random() * 80,
      yOffset: 10 + Math.random() * 80,
      size: Math.random() * 5 + 2,
      delay: Math.random() * 5,
    }));
    setSparkles(particles);

    // Preload background image
    const img = new Image();
    img.src = '/images/students.png';
    img.onload = () => setBgLoaded(true);
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showDetails) {
        setShowDetails(false);
      }
    };

    if (showDetails) {
      document.addEventListener('keydown', handleEscape);
      // Focus the close button when modal opens
      setTimeout(() => closeButtonRef.current?.focus(), 100);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showDetails]);

  // Throttled mouse move handler for better performance.
  // Store the target and pointer values immediately because React clears
  // e.currentTarget after the event callback, which can crash inside setTimeout.
  const throttleTimeout = useRef(null);

  useEffect(() => {
    return () => {
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
        throttleTimeout.current = null;
      }
    };
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (prefersReducedMotion || window.matchMedia?.('(pointer: coarse)').matches) return;

    const target = e.currentTarget;
    const clientX = e.clientX;
    const clientY = e.clientY;

    if (!target || throttleTimeout.current) return;

    throttleTimeout.current = setTimeout(() => {
      if (!target.isConnected) {
        throttleTimeout.current = null;
        return;
      }

      const rect = target.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        throttleTimeout.current = null;
        return;
      }

      setMousePos({
        x: ((clientX - rect.left) / rect.width) * 100,
        y: ((clientY - rect.top) / rect.height) * 100,
      });
      throttleTimeout.current = null;
    }, 50);
  }, [prefersReducedMotion]);

  return (
    <section
      className="relative w-full py-12 sm:py-16 md:py-20 overflow-hidden"
      style={{
        backgroundImage: bgLoaded ? "url('/images/students.png')" : 'none',
        backgroundColor: bgLoaded ? 'transparent' : '#1e293b',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
      onMouseMove={handleMouseMove}
      aria-labelledby="why-choose-heading"
    >
      {/* Enhanced dark overlay for better contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />

      {/* Sparkles - only if motion not reduced */}
      {!prefersReducedMotion && (
        <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none" aria-hidden="true">
          {sparkles.map((sparkle, index) => (
            <motion.div
              key={index}
              className="absolute bg-white rounded-full opacity-60 shadow-md"
              style={{
                width: sparkle.size,
                height: sparkle.size,
                top: `${sparkle.yOffset}%`,
                left: `${sparkle.xOffset}%`,
                filter: "blur(1px)",
              }}
              animate={{
                x: [(mousePos.x - 50) * 0.15, 0, (mousePos.x - 50) * -0.15, 0],
                y: [(mousePos.y - 50) * 0.15, 0, (mousePos.y - 50) * -0.15, 0],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{ 
                duration: 10, 
                repeat: Infinity, 
                repeatType: "mirror", 
                delay: sparkle.delay 
              }}
            />
          ))}
        </div>
      )}

      {/* Gradient blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply blur-3xl opacity-20" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply blur-3xl opacity-20" />
      </div>

      {/* Main content */}
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 z-10">
        <motion.div 
          className="text-center mb-8 sm:mb-12 md:mb-16" 
          initial={{ opacity: 0, y: -40 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-5">
            <Award className="w-4 h-4 mr-2" /> Our Advantages
          </div>
          <h2 
            id="why-choose-heading"
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight px-4 drop-shadow-2xl"
          >
            Why Choose{" "}
            <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent drop-shadow-none">
              The Federal Polytechnic Bida?
            </span>
          </h2>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
          variants={containerVariants} 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }}
          role="list"
        >
          {features.map((item, index) => (
            <motion.div 
              key={index} 
              variants={cardVariants} 
              whileHover={{ scale: 1.03, y: -5 }} 
              className="relative group w-full"
              role="listitem"
            >
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-blue-200 rounded-full opacity-20 group-hover:opacity-30 transition" aria-hidden="true" />
              <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-purple-200 rounded-full opacity-20 group-hover:opacity-30 transition" aria-hidden="true" />

              <div className="relative bg-white/95 backdrop-blur-xl border border-gray-100 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-6">
                <Quote className="absolute top-4 right-4 w-6 h-6 sm:w-8 sm:h-8 text-blue-100" aria-hidden="true" />
                
                <div className="flex justify-center mb-4" aria-hidden="true">{item.icon}</div>
                
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 text-center">
                  {item.title}
                </h3>
                
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-6 text-center">
                  {item.description}
                </p>

                <button
                  onClick={() => {
                    setSelected(item);
                    setShowDetails(true);
                  }}
                  aria-label={`Learn more about ${item.title}`}
                  className="w-full inline-flex items-center justify-center px-4 py-2 rounded-full bg-blue-50 text-blue-700 font-semibold text-xs sm:text-sm hover:bg-blue-100 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Learn More
                  <ChevronRight className="w-3 h-3 ml-1" />
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Accessible Modal */}
      <AnimatePresence>
        {showDetails && selected && (
          <motion.div 
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            onClick={() => setShowDetails(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <motion.div 
              ref={modalRef}
              className="relative bg-white rounded-3xl p-6 sm:p-8 w-full max-w-3xl max-h-[85vh] overflow-y-auto" 
              initial={{ scale: 0.8 }} 
              animate={{ scale: 1 }} 
              exit={{ scale: 0.8 }} 
              transition={{ duration: 0.3 }} 
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                ref={closeButtonRef}
                onClick={() => setShowDetails(false)}
                aria-label="Close modal"
                className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center z-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>

              <div className="text-center mb-6">
                <div className="flex justify-center mb-4" aria-hidden="true">{selected.icon}</div>
                <h3 id="modal-title" className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  {selected.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600">{selected.description}</p>
              </div>

              <p id="modal-description" className="text-sm sm:text-base text-gray-700 leading-relaxed text-justify">
                {selected.detailContent}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default WhyChooseUs;